import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import {
  ArrowLeft,
  Camera,
  CameraOff,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Search,
  Send,
  X,
  User,
  ScanLine,
  RefreshCcw,
  Hand,
  BadgeCheck,
  Ticket,
  MapPin,
  Clock3,
  ScrollText,
} from "lucide-react";
import {
  getEventosConBoletosEmitidos,
  hidratarEventosRecepcion,
  validarBoletoPorQR,
  validarBoletoManual,
  buscarAsistentes,
  listarAccesos,
  isRecepcionAutenticado,
} from "@/data/recepcionStore";
import { RECEPCION } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import { crearControlEscaneo, DURACION_RESULTADO_MS } from "@/utils/controlEscaneo";
import { crearControlCamara, ESTADO_CAMARA_INICIAL } from "@/utils/controlCamara";
import { CamaraEstado } from "@/screens/reception/CamaraEstado";

const QR_REGION_ID = "ct-recepcion-qr-region";
const CONFIG_ESCANEO = {
  fps: 12,
  qrbox: (viewfinderWidth, viewfinderHeight) => {
    const size = Math.min(viewfinderWidth, viewfinderHeight) * 0.72;
    return { width: size, height: size };
  },
  aspectRatio: 1.0,
};

/**
 * Portal de Recepción · Escáner.
 *
 * Fase C:
 * - Selector de cámaras cuando hay más de una disponible.
 * - Contadores: listos (evento), validados (sesión), manuales, rechazos,
 *   advertencias.
 * - Flash OK ultrarrápido (<1s) para permitir escaneo continuo de grupos.
 * - Búsqueda tolerante a acentos por nombre / apellido / número de socio.
 * - Autorización manual desde la búsqueda queda registrada en la bitácora
 *   (el backend etiqueta el método como "MANUAL").
 */
export default function Escaner() {
  const { eventoId } = useParams();
  const navigate = useNavigate();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!isRecepcionAutenticado()) {
      navigate("/recepcion/login", { replace: true });
      return;
    }
    hidratarEventosRecepcion().then(() => setTick((t) => t + 1));
  }, [navigate, eventoId]);

  const eventos = useMemo(() => getEventosConBoletosEmitidos(), [tick]);
  const eventoRef = useMemo(
    () => eventos.find((e) => e.eventoId === eventoId) || null,
    [eventos, eventoId],
  );

  // UI state
  const [flash, setFlash] = useState(null);
  const [contador, setContador] = useState({
    ok: 0,
    okManual: 0,
    adv: 0,
    rec: 0,
  });
  const [simulado, setSimulado] = useState("");
  const [busquedaQuery, setBusquedaQuery] = useState("");
  const [accesos, setAccesos] = useState([]);
  const [cargandoAccesos, setCargandoAccesos] = useState(false);

  const controlRef = useRef(crearControlEscaneo());
  const dismissTimerRef = useRef(null);
  const flashSeqRef = useRef(0);

  const registrarResultado = useCallback((res) => {
    setContador((c) => {
      if (res.resultado === "OK") {
        const esManual =
          res.boleto && res.boleto.metodoValidacion === "MANUAL";
        return {
          ...c,
          ok: c.ok + 1,
          okManual: esManual ? c.okManual + 1 : c.okManual,
        };
      }
      if (res.resultado === "ADVERTENCIA") return { ...c, adv: c.adv + 1 };
      return { ...c, rec: c.rec + 1 };
    });
    // Refrescar contadores del evento tras cada validación.
    hidratarEventosRecepcion().then(() => setTick((t) => t + 1));
  }, []);

  // Cada resultado dura DURACION_RESULTADO_MS; un temporizador sólo cierra
  // el resultado que lo programó (nunca uno posterior).
  const mostrarResultado = useCallback(
    (res, solicitudId) => {
      registrarResultado(res);
      if (!controlRef.current.puedeMostrar(solicitudId)) return;
      const id = ++flashSeqRef.current;
      setFlash({ ...res, _id: id });
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(() => {
        setFlash((f) => (f && f._id === id ? null : f));
      }, DURACION_RESULTADO_MS);
    },
    [registrarResultado],
  );

  // ----- Cámara -----
  const [cam, setCam] = useState(ESTADO_CAMARA_INICIAL);
  const [elegida, setElegida] = useState("");
  const camRef = useRef(null);
  const alLeerRef = useRef(null);
  alLeerRef.current = async (decodedText) => {
    const id = controlRef.current.lectura(decodedText);
    if (id == null) return;
    const res = await validarBoletoPorQR(decodedText, { eventoId });
    mostrarResultado(res, id);
  };

  useEffect(() => {
    const control = crearControlCamara({
      crearEscaner: () => new Html5Qrcode(QR_REGION_ID, { verbose: false }),
      configEscaneo: CONFIG_ESCANEO,
      alLeer: (texto) => alLeerRef.current(texto),
      obtenerVideo: () => document.querySelector(`#${QR_REGION_ID} video`),
      notificar: setCam,
    });
    camRef.current = control;
    control.cargarCamaras();
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      control.destruir();
    };
  }, []);

  const camaraEncendida = cam.fase !== "inactiva" && cam.fase !== "error";
  const iniciarCamara = () => camRef.current.iniciar(elegida || undefined);
  const detenerCamara = () => camRef.current.detener();
  const cambiarCamara = (nuevoId) => {
    setElegida(nuevoId);
    if (camaraEncendida) camRef.current.iniciar(nuevoId || undefined);
  };

  // Simular escaneo (dev only)
  const simularEscaneo = async () => {
    const token = simulado.trim();
    if (!token) return;
    const id = controlRef.current.nuevaSolicitud();
    const res = await validarBoletoPorQR(token, { eventoId });
    mostrarResultado(res, id);
    setSimulado("");
  };

  // Búsqueda manual (debounced)
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  useEffect(() => {
    if (busquedaQuery.trim().length < 2) {
      setResultados([]);
      setBuscando(false);
      return;
    }
    let cancelado = false;
    setBuscando(true);
    const t = setTimeout(async () => {
      const r = await buscarAsistentes(eventoId, busquedaQuery);
      if (!cancelado) {
        setResultados(r);
        setBuscando(false);
      }
    }, 200);
    return () => {
      cancelado = true;
      clearTimeout(t);
    };
  }, [eventoId, busquedaQuery, contador]);

  // ----- Bitácora de accesos (en pantalla) -----
  const refrescarAccesos = useCallback(async () => {
    if (!eventoId) return;
    setCargandoAccesos(true);
    const rows = await listarAccesos(eventoId);
    setAccesos(rows);
    setCargandoAccesos(false);
  }, [eventoId]);

  // Carga inicial y refresco automático tras cada validación (contador cambia).
  useEffect(() => {
    refrescarAccesos();
  }, [refrescarAccesos, contador]);

  // El selector refleja la cámara activa real; sin id verificable no se elige ninguna.
  const valorSelector = camaraEncendida && cam.fase !== "iniciando"
    ? cam.camaraActivaId && cam.camaras.some((c) => c.id === cam.camaraActivaId)
      ? cam.camaraActivaId
      : ""
    : elegida;

  if (!eventoRef) {
    return <Navigate to="/recepcion" replace />;
  }

  const validarManual = async (boletoId) => {
    const id = controlRef.current.nuevaSolicitud();
    const res = await validarBoletoManual(boletoId, { eventoId });
    mostrarResultado(res, id);
  };

  const forzarValidacion = async () => {
    if (!flash || !flash.boleto) return;
    const id = controlRef.current.nuevaSolicitud();
    const res = flash.boleto.qrToken
      ? await validarBoletoPorQR(flash.boleto.qrToken, {
          eventoId,
          forzar: true,
        })
      : await validarBoletoManual(flash.boleto.id, {
          eventoId,
          forzar: true,
        });
    mostrarResultado(res, id);
  };

  const cerrarFlash = () => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    setFlash(null);
  };

  return (
    <div
      data-testid={RECEPCION.escanerScreen}
      className="flex flex-col gap-6 pb-8"
    >
      {/* Header + cambio de evento + contadores */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          data-testid={RECEPCION.btnCambiarEvento}
          onClick={async () => {
            await detenerCamara();
            navigate("/recepcion");
          }}
          className="self-start inline-flex items-center gap-2 rounded-full pl-2 pr-4 py-2 text-sm font-medium border hairline bg-card text-foreground hover:bg-muted transition-colors"
        >
          <span className="w-7 h-7 grid place-items-center rounded-full bg-primary/10 dark:bg-accent/15 text-primary dark:text-accent">
            <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          </span>
          Cambiar evento
        </button>

        <div
          data-testid={RECEPCION.contadorSesion}
          className="flex items-center gap-1.5 sm:gap-2 text-xs flex-wrap"
        >
          <span
            data-testid={RECEPCION.contadorListos}
            className="inline-flex items-center gap-1 rounded-full px-2 py-1 border hairline bg-card text-muted-foreground"
            title="Boletos listos del evento"
          >
            <Ticket className="w-3.5 h-3.5" /> {eventoRef.listos}
          </span>
          <span
            data-testid={RECEPCION.contadorValidados}
            className="inline-flex items-center gap-1 rounded-full px-2 py-1 border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
            title="Accesos autorizados en esta sesión"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> {contador.ok}
          </span>
          <span
            data-testid={RECEPCION.contadorManuales}
            className="inline-flex items-center gap-1 rounded-full px-2 py-1 border border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300"
            title="Validaciones manuales"
          >
            <Hand className="w-3.5 h-3.5" /> {contador.okManual}
          </span>
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-1 border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
            title="Advertencias que requieren revisión"
          >
            <AlertTriangle className="w-3.5 h-3.5" /> {contador.adv}
          </span>
          <span
            data-testid={RECEPCION.contadorRechazos}
            className="inline-flex items-center gap-1 rounded-full px-2 py-1 border border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300"
            title="Accesos rechazados"
          >
            <XCircle className="w-3.5 h-3.5" /> {contador.rec}
          </span>
        </div>
      </div>

      <header className="animate-fade-up">
        <span className="overline text-accent">Validación de accesos</span>
        <h1
          data-testid={RECEPCION.eventoActivo}
          className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-foreground"
        >
          {eventoRef.nombreEvento}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {[eventoRef.fechaEvento, eventoRef.horaEvento, eventoRef.salon]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </header>

      {/* Zona escáner */}
      <section className="rounded-2xl border hairline bg-card p-5 md:p-6 shadow-soft animate-fade-up delay-100 flex flex-col gap-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ScanLine className="w-4 h-4 text-accent" strokeWidth={1.8} />
            <span className="overline text-accent">Escáner de QR</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {cam.camaras.length > 1 && (
              <select
                data-testid={RECEPCION.cameraSelect}
                value={valorSelector}
                onChange={(e) => cambiarCamara(e.target.value)}
                className="rounded-full border hairline bg-card px-3 py-1.5 text-xs text-foreground focus:ring-2 focus:ring-accent/40 focus:outline-none"
              >
                <option value="" data-testid={RECEPCION.cameraOptionAuto}>
                  {camaraEncendida && cam.fase !== "iniciando" && !cam.camaraActivaId
                    ? "Cámara activa no identificada"
                    : "Automática (trasera)"}
                </option>
                {cam.camaras.map((c, i) => (
                  <option
                    key={c.id}
                    value={c.id}
                    data-testid={RECEPCION.cameraOption(c.id)}
                  >
                    {c.label || `Cámara ${i + 1}`}
                  </option>
                ))}
              </select>
            )}
            {camaraEncendida ? (
              <button
                type="button"
                data-testid={RECEPCION.btnStopCamera}
                onClick={detenerCamara}
                className="inline-flex items-center justify-center gap-2 rounded-full px-4 min-h-[44px] text-xs font-medium border hairline bg-card hover:bg-muted transition-colors"
              >
                <CameraOff className="w-3.5 h-3.5" strokeWidth={1.8} />
                Detener
              </button>
            ) : (
              <button
                type="button"
                data-testid={RECEPCION.btnStartCamera}
                onClick={iniciarCamara}
                className="inline-flex items-center justify-center gap-2 rounded-full px-4 min-h-[44px] text-xs font-medium bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft hover:opacity-90 transition-opacity"
              >
                <Camera className="w-3.5 h-3.5" strokeWidth={1.8} />
                Activar cámara
              </button>
            )}
          </div>
        </div>

        {/* Espacio reservado: el resultado nunca tapa el video ni lo desplaza. */}
        <div
          data-testid={RECEPCION.resultadoSlot}
          className="mx-auto w-full max-w-[480px] min-h-[180px] sm:min-h-[164px]"
        >
          {flash && (
            <FlashResultado
              flash={flash}
              onClose={cerrarFlash}
              onForzar={forzarValidacion}
            />
          )}
        </div>

        <div
          data-testid={RECEPCION.scannerContainer}
          className="relative mx-auto w-full max-w-[480px] min-h-[280px] rounded-2xl overflow-hidden bg-muted grid place-items-center"
        >
          <div id={QR_REGION_ID} />
          <CamaraEstado cam={cam} onReintentar={iniciarCamara} />
        </div>

        {/* Simulador dev-only */}
        <div className="rounded-xl border border-dashed hairline p-4 flex flex-col gap-3 bg-background/60">
          <div className="flex items-center gap-2">
            <span className="overline text-muted-foreground">
              Simular escaneo (solo desarrollo)
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={simulado}
              onChange={(e) => setSimulado(e.target.value)}
              placeholder="Pega aquí un qrToken para simular"
              data-testid={RECEPCION.simInput}
              className="flex-1 min-w-0 rounded-full border hairline bg-card px-4 min-h-[44px] text-sm outline-none focus:ring-2 focus:ring-accent/40"
              onKeyDown={(e) => {
                if (e.key === "Enter") simularEscaneo();
              }}
            />
            <button
              type="button"
              data-testid={RECEPCION.btnSimular}
              onClick={simularEscaneo}
              disabled={!simulado.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-full px-4 min-h-[44px] text-sm font-medium bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 shadow-soft hover:opacity-90 transition-opacity disabled:opacity-40"
            >
              <Send className="w-3.5 h-3.5" strokeWidth={1.8} />
              Simular
            </button>
          </div>
        </div>
      </section>

      {/* Búsqueda manual (Fase C) */}
      <section
        data-testid={RECEPCION.btnBusquedaManual}
        className="rounded-2xl border hairline bg-card p-5 md:p-6 shadow-soft animate-fade-up delay-200 flex flex-col gap-4"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-accent" strokeWidth={1.8} />
            <span className="overline text-accent">
              Búsqueda manual (sin QR)
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Nombre · Apellido · Número de socio
          </p>
        </div>

        <input
          type="text"
          value={busquedaQuery}
          onChange={(e) => setBusquedaQuery(e.target.value)}
          placeholder="Buscar por nombre, apellido o número de socio…"
          data-testid={RECEPCION.busquedaInput}
          autoComplete="off"
          className="w-full rounded-full border hairline bg-background px-4 min-h-[44px] text-sm outline-none focus:ring-2 focus:ring-accent/40"
        />

        <ul
          data-testid={RECEPCION.busquedaResultados}
          className="flex flex-col gap-2"
        >
          {busquedaQuery.trim().length < 2 && (
            <li className="text-xs text-muted-foreground px-1">
              Escribe al menos 2 letras para buscar.
            </li>
          )}
          {busquedaQuery.trim().length >= 2 &&
            !buscando &&
            resultados.length === 0 && (
              <li className="text-xs text-muted-foreground px-1">
                Sin coincidencias para este evento.
              </li>
            )}
          {buscando && (
            <li className="text-xs text-muted-foreground px-1 flex items-center gap-1.5">
              <RefreshCcw className="w-3 h-3 animate-spin" strokeWidth={1.8} />
              Buscando…
            </li>
          )}
          {resultados.map((b) => (
            <SearchResultRow
              key={b.id}
              boleto={b}
              onValidar={() => validarManual(b.id)}
            />
          ))}
        </ul>
      </section>

      {/* Bitácora de accesos (en pantalla) */}
      <section
        data-testid={RECEPCION.bitacoraSection}
        className="rounded-2xl border hairline bg-card p-5 md:p-6 shadow-soft animate-fade-up delay-200 flex flex-col gap-4"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-accent" strokeWidth={1.8} />
            <span className="overline text-accent">Bitácora de accesos</span>
          </div>
          <button
            type="button"
            data-testid={RECEPCION.bitacoraRefresh}
            onClick={refrescarAccesos}
            className="inline-flex items-center justify-center gap-2 rounded-full px-4 min-h-[44px] text-xs font-medium border hairline bg-card hover:bg-muted transition-colors"
          >
            <RefreshCcw
              className={cn("w-3.5 h-3.5", cargandoAccesos && "animate-spin")}
              strokeWidth={1.8}
            />
            Actualizar
          </button>
        </div>

        {accesos.length === 0 ? (
          <p
            data-testid={RECEPCION.bitacoraVacia}
            className="text-xs text-muted-foreground px-1"
          >
            Aún no hay accesos registrados para este evento.
          </p>
        ) : (
          <ul
            data-testid={RECEPCION.bitacoraLista}
            className="flex flex-col gap-2 max-h-[420px] overflow-y-auto pr-1"
          >
            {accesos.map((a) => (
              <BitacoraRow key={a.id} acceso={a} />
            ))}
          </ul>
        )}
      </section>

    </div>
  );
}

/* -------- Result row (búsqueda) -------- */
function SearchResultRow({ boleto, onValidar }) {
  const validado = boleto.estado === "VALIDADO";
  const sinAsignar = !boleto.asistenteNombre;
  const badge = validado
    ? { label: "Validado", tone: "emerald" }
    : sinAsignar
      ? { label: "Sin asignar", tone: "amber" }
      : { label: "Listo", tone: "sky" };
  const toneClass = {
    emerald:
      "text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/40",
    amber:
      "text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/40",
    sky: "text-sky-700 dark:text-sky-300 bg-sky-500/10 border-sky-500/40",
  }[badge.tone];

  return (
    <li
      data-testid={RECEPCION.busquedaItem(boleto.id)}
      className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border hairline bg-background/60 px-4 py-3"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm text-foreground">
          <User className="w-4 h-4 text-accent shrink-0" strokeWidth={1.6} />
          <span className="truncate font-medium">
            {boleto.asistenteNombre || "Sin nombre asignado"}
          </span>
          <span
            className={cn(
              "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium",
              toneClass,
            )}
          >
            {badge.label}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <BadgeCheck className="w-3 h-3" strokeWidth={1.7} />
            Socio {boleto.numeroSocio || boleto.socioId || "—"}
          </span>
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" strokeWidth={1.7} />
            {boleto.mesa != null ? `Mesa ${boleto.mesa}` : boleto.salon || "—"}
          </span>
          <span className="inline-flex items-center gap-1">
            <Ticket className="w-3 h-3" strokeWidth={1.7} />
            {boleto.consecutivo}/{boleto.totalBoletos}
          </span>
          {boleto.socioTitular &&
            boleto.socioTitular !== boleto.asistenteNombre && (
              <span className="truncate">Titular: {boleto.socioTitular}</span>
            )}
          {validado && boleto.fechaValidacion && (
            <span className="inline-flex items-center gap-1">
              <Clock3 className="w-3 h-3" strokeWidth={1.7} />
              {new Date(boleto.fechaValidacion).toLocaleTimeString("es-MX", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        data-testid={RECEPCION.btnValidarManual(boleto.id)}
        onClick={onValidar}
        disabled={validado}
        className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full px-4 min-h-[44px] text-xs font-medium bg-primary text-primary-foreground dark:bg-accent dark:text-accent-foreground ring-1 ring-primary/20 dark:ring-accent/40 hover:opacity-90 disabled:opacity-40 transition-opacity"
      >
        <Hand className="w-3.5 h-3.5" strokeWidth={1.8} />
        {validado ? "Ya validado" : "Autorizar acceso"}
      </button>
    </li>
  );
}

/* -------- Fila de bitácora de accesos -------- */
function BitacoraRow({ acceso }) {
  const tone =
    acceso.resultado === "OK"
      ? {
          cls: "text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/40",
          Icon: CheckCircle2,
        }
      : acceso.resultado === "ADVERTENCIA"
        ? {
            cls: "text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/40",
            Icon: AlertTriangle,
          }
        : {
            cls: "text-red-700 dark:text-red-300 bg-red-500/10 border-red-500/40",
            Icon: XCircle,
          };
  const ubicacion =
    acceso.mesa != null ? `Mesa ${acceso.mesa}` : acceso.salon || "—";
  const hora = acceso.fecha
    ? new Date(acceso.fecha).toLocaleTimeString("es-MX", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  return (
    <li
      data-testid={RECEPCION.bitacoraItem(acceso.id)}
      className="flex items-start gap-3 rounded-xl border hairline bg-background/60 px-4 py-3"
    >
      <span
        className={cn(
          "shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium",
          tone.cls,
        )}
      >
        <tone.Icon className="w-3 h-3" strokeWidth={1.8} />
        {acceso.resultado}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm text-foreground">
          <User className="w-4 h-4 text-accent shrink-0" strokeWidth={1.6} />
          <span className="truncate font-medium">
            {acceso.asistenteNombre || "Sin nombre"}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" strokeWidth={1.7} />
            {ubicacion}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock3 className="w-3 h-3" strokeWidth={1.7} />
            {hora}
          </span>
          <span className="inline-flex items-center gap-1">
            {acceso.metodo === "MANUAL" ? (
              <Hand className="w-3 h-3" strokeWidth={1.7} />
            ) : (
              <ScanLine className="w-3 h-3" strokeWidth={1.7} />
            )}
            {acceso.metodo}
          </span>
          {acceso.usuarioRecepcion && (
            <span className="truncate">Por {acceso.usuarioRecepcion}</span>
          )}
        </div>
        {acceso.resultado !== "OK" && acceso.motivo && (
          <p className="mt-1 text-[11px] text-muted-foreground truncate">
            {acceso.motivo}
          </p>
        )}
      </div>
    </li>
  );
}

/* -------- Flash a pantalla completa -------- */
function FlashResultado({ flash, onClose, onForzar }) {
  const isOk = flash.resultado === "OK";
  const isAdv = flash.resultado === "ADVERTENCIA";

  const bgClass = isOk
    ? "bg-emerald-500"
    : isAdv
      ? "bg-amber-500"
      : "bg-red-600";
  const testId = isOk
    ? RECEPCION.flashOk
    : isAdv
      ? RECEPCION.flashAdvertencia
      : RECEPCION.flashRechazado;
  const Icon = isOk ? CheckCircle2 : isAdv ? AlertTriangle : XCircle;
  const titulo = isOk
    ? "ACCESO AUTORIZADO"
    : isAdv
      ? "REVISAR MANUALMENTE"
      : "ACCESO RECHAZADO";

  return (
    <div
      data-testid={testId}
      role="status"
      onClick={onClose}
      className={`relative overflow-hidden rounded-2xl ${bgClass} text-white animate-fade-in px-4 pt-3 pb-4 flex flex-col gap-2.5 cursor-pointer`}
    >
      <button
        type="button"
        data-testid={RECEPCION.btnCerrarFlash}
        aria-label="Continuar"
        title="Continuar"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-2 right-2 inline-flex items-center justify-center rounded-full w-11 h-11 bg-black/25 text-white hover:bg-black/40 transition-colors"
      >
        <X className="w-4 h-4" strokeWidth={1.8} />
      </button>
      <div className="flex items-center gap-2.5 pr-12">
        <Icon className="w-7 h-7 shrink-0" strokeWidth={1.6} />
        <h2 className="font-heading text-lg sm:text-2xl tracking-tight leading-tight">
          {titulo}
        </h2>
      </div>
      <p
        data-testid={RECEPCION.flashMotivo}
        className="-mt-1 text-white/90 text-sm leading-snug break-words"
      >
        {flash.motivo}
      </p>
      <div
        key={flash._id}
        data-testid={RECEPCION.flashCuenta}
        aria-hidden="true"
        className="absolute bottom-0 inset-x-0 h-1.5 bg-white/70 origin-left"
        style={{ animation: `ct-cuenta-resultado ${DURACION_RESULTADO_MS}ms linear forwards` }}
      />
      <div className="flex flex-wrap items-center gap-2">
        {flash.boleto && flash.boleto.asistenteNombre && (
          <div
            data-testid={RECEPCION.flashAsistente}
            className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 bg-black/20 text-white text-sm"
          >
            <User className="w-4 h-4" strokeWidth={1.8} />
            {flash.boleto.asistenteNombre}
            {flash.boleto.mesa != null
              ? ` · Mesa ${flash.boleto.mesa}`
              : flash.boleto.salon
                ? ` · ${flash.boleto.salon}`
                : ""}
          </div>
        )}
        {isAdv && flash.boleto && !flash.boleto.asistenteNombre && (
          <button
            type="button"
            data-testid={RECEPCION.btnForzarValidacion}
            onClick={(e) => {
              e.stopPropagation();
              onForzar();
            }}
            className="inline-flex items-center justify-center gap-2 rounded-full px-4 min-h-[44px] text-sm font-medium bg-white text-foreground hover:bg-white/90 transition-colors"
          >
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />
            Validar de todos modos
          </button>
        )}
      </div>
    </div>
  );
}
